import { Accordion, Badge, Grid, Heading, Text, DesignLanguage, useIsDesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import clsx from 'clsx'
import React, { FunctionComponent, ReactNode } from 'react'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { AddressModeType, CheckoutStatus, ShippingOptionStatus, ExpandableComponent } from '../../model'
import { TOGGLE_STATES, TrackingEvent } from '../../model/monitoring'

import {
  CheckoutState,
  useAppRouter,
  useCheckoutState,
  useMerchantDisplayNameOrName,
  useIsCheckoutLoadingOrUpdating,
  useIsExpress,
  useIsConsumerLendingOrder,
  useIsPickup,
  useIsShippingOptionRequired,
  useItemsAndSubscriptions,
  useShippingOption,
  useInitialData,
  useIsAfterpayAppGiftCardFlow,
  useSummaryLayout,
  useExpandedComponentControl,
  useIsBillingAgreementApprovalProcess,
  useGiftCardPromoBreakdown,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { Money } from '../Money'
import { GiftCardPromoBreakdown } from '../GiftCardPromoBreakdown'
import { OrderDetails } from '../OrderDetails'

import styles from './style.module.css'
import { OrderConversionBreakdown } from '../OrderConversionBreakdown'
import { useTranslation } from '../../utils/convergence'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { Money as MoneyType } from '@afterpay/types'
import { useMoney } from '../Money/useMoney'

interface OrderSummaryProps {
  testNameSpace?: string
  showOrderDetails?: boolean
  narrowMargin?: boolean
  showItemCountHeader?: boolean
}
export const OrderSummaryPromo: FunctionComponent<{ orderAmount: MoneyType | undefined; showAmount?: boolean }> = ({ orderAmount, showAmount = true }) => {
  const checkoutState: CheckoutState = useCheckoutState()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const promotion = checkoutState.started?.promotion
  const hasPromo = valueIsNotNullOrUndefined(promotion)
  const { locale } = useAppRouter()
  const { t } = useTranslation()
  const { sectionHeadingSize } = useCheckoutSizes()
  const { formatMoney } = useMoney({
    locale,
    currency: checkoutState.started?.promotion?.discount?.currency,
    decimalPlaces: isArcadeDesignLanguage ? 0 : 2,
  })

  if (!hasPromo || isArcadeDesignLanguage) {
    return null
  }

  return (
    <>
      <div className={styles.promo} data-testid='promo-container'>
        <div>
          <Badge icon='Afterpay' text={`${formatMoney(promotion.discount.amount)} ${t('common:discountApplied')}`} />
        </div>
        {showAmount && (
          <div>
            <Text color='Gray40' renderAs='span'>
              <Money size={sectionHeadingSize} strikethrough value={orderAmount} />
            </Text>
          </div>
        )}
      </div>
      <TrackOnMount eventName={TrackingEvent.VIEWED_PROMO} />
    </>
  )
}

export const OrderSummary: FunctionComponent<OrderSummaryProps> = ({
  showOrderDetails = true,
  testNameSpace,
  narrowMargin = false,
  showItemCountHeader = false,
}) => {
  const checkoutState: CheckoutState = useCheckoutState()
  const merchantDisplayNameOrName = useMerchantDisplayNameOrName()
  const shippingOptionState = useShippingOption()
  const { t } = useTranslation()
  const { setExpandedState, expandableComponentState } = useExpandedComponentControl(ExpandableComponent.ORDER_SUMMARY)
  const isExpanded = expandableComponentState === 'Expanded'
  const { locale } = useAppRouter()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { sectionHeadingSize, textSize, smallTextSize } = useCheckoutSizes()
  const itemsAndSubscriptions = useItemsAndSubscriptions()
  const isPickup = useIsPickup()
  const isExpress = useIsExpress()
  const isShippingOptionRequired = useIsShippingOptionRequired()
  const isCheckoutLoadingOrUpdating = useIsCheckoutLoadingOrUpdating()
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const { addressMode } = useInitialData()
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const giftCardPromotion = useGiftCardPromoBreakdown()
  const designLanguageClass = useDesignLanguagePerClass(styles)
  const { showProfileCard } = useSummaryLayout()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()

  const wrapperClassNames = clsx(
    !isAfterpayAppGiftCardFlow && designLanguageClass.wrapper,
    isConsumerLendingOrder && styles.consumerLendingWrapper,
    isExpanded && styles.expandedWrapper,
    !showProfileCard && styles.hasProfileCardRemoved
  )

  const noAccordionWrapperClassNames = clsx(narrowMargin ? styles.narrowWrapper : styles.header)

  const orderAmount = checkoutState.started?.consumerAmount ?? checkoutState.checkout?.amount
  const summaryAmount = checkoutState.started?.promotion?.owedAfterDiscount ?? orderAmount
  const hasOrderDetails = itemsAndSubscriptions?.length > 0 || shippingOptionState?.selectedShippingOption !== undefined
  const handleChange = (): void => {
    const newState = !isExpanded
    logEvent(TrackingEvent.TOGGLED_ORDER_ITEMS, {
      orderItems: itemsAndSubscriptions,
      doItemPricesAddUp: checkoutState.doItemsReconcile,
      toggledValue: newState ? TOGGLE_STATES.EXPANDED : TOGGLE_STATES.COLLAPSED,
    })
    setExpandedState(isExpanded ? 'Collapsed' : 'Expanded')
  }

  const getShippingInfoText = (): string => {
    if (isPickup) {
      return t('summary:handlingInfo')
    }
    if (isShippingOptionRequired || addressMode === AddressModeType.SHIP_TO_ORDER_ADDRESS_WITHOUT_SHIPPING_OPTIONS) {
      return t('summary:postSelectShippingInfo')
    }
    return t('summary:deferredShippingInfo')
  }

  const renderShippingAndTaxHeaderInfo = (): ReactNode => {
    const checkoutStarted = checkoutState.status >= CheckoutStatus.Started
    if (!checkoutStarted || !isExpress) {
      return null
    }

    const shippingOptionSelectAttempted =
      isShippingOptionRequired &&
      shippingOptionState?.shippingOptionsStatus >= ShippingOptionStatus.UpdatingSelected &&
      !(shippingOptionState?.shippingOptionsStatus === ShippingOptionStatus.Error)
    if (shippingOptionSelectAttempted || isPickup || !isShippingOptionRequired) {
      const info = getShippingInfoText()
      const textColor = isBronteDesignLanguage ? 'Black' : 'Subtle'
      const shippingInfoTextSize = isBronteDesignLanguage ? textSize : smallTextSize
      return (
        <div className={designLanguageClass.postSelectShippingInfo}>
          <Text color={textColor} size={shippingInfoTextSize}>
            {info}
          </Text>
        </div>
      )
    }
    return null
  }

  const renderSummaryHeader = (): ReactNode => {
    const merchantNameHeadingText =
      isBillingAgreementApprovalProcess || isAfterpayAppGiftCardFlow || !isBronteDesignLanguage
        ? merchantDisplayNameOrName
        : [merchantDisplayNameOrName, t('common:summary')].join(' ')
    const headingText = showItemCountHeader ? merchantDisplayNameOrName : isConsumerLendingOrder ? t('common:orderSummary') : merchantNameHeadingText
    const bold = isBronteDesignLanguage
    const itemCount = itemsAndSubscriptions?.reduce((sum, item) => sum + ('quantity' in item ? item.quantity : 1), 0) ?? 0

    return (
      <>
        <Grid>
          <Heading size={sectionHeadingSize} renderAs='span' testNameSpace='summary-merchant-display-name'>
            {headingText}
          </Heading>
          {!showItemCountHeader && (isBronteDesignLanguage || isConsumerLendingOrder) && (
            <Grid.FixedItem width={108} align='right'>
              <Money
                locale={locale}
                size={textSize}
                bold={bold}
                value={summaryAmount}
                loading={isCheckoutLoadingOrUpdating}
                testNameSpace='orderSummary-header-total-amount'
              />
            </Grid.FixedItem>
          )}
        </Grid>
        {showItemCountHeader && itemCount > 0 && (
          <Text size={smallTextSize} color='Gray40' data-testid='orderSummary-item-count'>
            {t('common:itemCount', { count: itemCount })}
          </Text>
        )}
        {addressMode !== AddressModeType.NO_ADDRESS && renderShippingAndTaxHeaderInfo()}
        <OrderConversionBreakdown />
        <OrderSummaryPromo orderAmount={orderAmount} showAmount={!showItemCountHeader} />
      </>
    )
  }

  const contentInDetail = isBillingAgreementApprovalProcess || !showOrderDetails ? false : hasOrderDetails

  const summary = contentInDetail ? (
    <div className={wrapperClassNames}>
      <Accordion onChange={handleChange} expanded={isExpanded} testNameSpace={testNameSpace} id={ExpandableComponent.ORDER_SUMMARY}>
        <Accordion.Button>{renderSummaryHeader()}</Accordion.Button>
        <Accordion.Details>
          <OrderDetails />
        </Accordion.Details>
      </Accordion>
    </div>
  ) : (
    <div className={noAccordionWrapperClassNames}>{renderSummaryHeader()}</div>
  )

  return (
    <>
      {summary}
      {isBronteDesignLanguage && giftCardPromotion && (
        <div className={styles.giftCardPromotion}>
          <GiftCardPromoBreakdown promotion={giftCardPromotion} loading={isCheckoutLoadingOrUpdating} />
        </div>
      )}
    </>
  )
}
